// Site imagery manifest (PRP-14). One entry per placed asset ID from
// docs/design/image-asset-list.md. Files are optimized WebP under public/ at
// `${base}-${width}.webp` (heroes also have a phone crop at `${base}-m-${width}.webp`).
// To swap an image, replace its master, rerun scripts/optimize-images.sh and keep the ID.
//
// Direction (2026-10-12, docs/design/real-photo-assessment.md): real freshSTART photos
// wherever people appear, people-free documentary photos for rooms, food and supplies
// (design-assets/build-real-images.sh). Slots still on the PRP-14 cartoons are marked below.

export interface SiteImageEntry {
  id: string;
  /** Public path without the width suffix, e.g. /images/hero/hero-home */
  base: string;
  /** Widths generated by scripts/optimize-images.sh, ascending. */
  widths: readonly number[];
  /** Phone-crop widths (heroes only). */
  mobileWidths?: readonly number[];
  /** Intrinsic size of the largest width (sets the aspect ratio; prevents layout shift). */
  width: number;
  height: number;
  /** Empty for decorative images. */
  alt: string;
  decorative?: boolean;
  /** CSS object-position for cropped placements: keeps people's faces in frame. */
  focus?: string;
}

const HERO_WIDTHS = [640, 1280, 1920, 2560] as const;
const HERO_MOBILE = [640, 1280] as const;
const CONTENT = [640, 1280] as const;
const ILL = [320, 640] as const;

const hero = (id: string, name: string): SiteImageEntry => ({
  id,
  base: `/images/hero/${name}`,
  widths: HERO_WIDTHS,
  mobileWidths: HERO_MOBILE,
  width: 2560,
  height: 1097,
  alt: '',
  decorative: true,
});

const content = (id: string, base: string, width: number, height: number, alt: string): SiteImageEntry => ({
  id,
  base,
  widths: CONTENT,
  width,
  height,
  alt,
  decorative: alt === '',
});

/** A real freshSTART photo cut from the Facebook posts (about 2:1, framed on faces): too
 *  small to publish above 640 wide. `focus` is where faces sit, for tighter crops. */
const real = (id: string, base: string, height: number, focus: string, alt: string): SiteImageEntry => ({
  id,
  base,
  widths: [640],
  width: 640,
  height,
  alt,
  focus,
});

const illustration = (id: string, name: string): SiteImageEntry => ({
  id,
  base: `/images/illustrations/${name}`,
  widths: ILL,
  width: 640,
  height: 640,
  alt: '',
  decorative: true,
});

export const IMAGES = {
  // Hero banners (decorative; the headline carries the meaning)
  'HERO-01': hero('HERO-01', 'hero-home'),
  // Real event photo (1536 px source): no 1920/2560 widths and a single phone crop.
  'HERO-02': { ...hero('HERO-02', 'hero-about'), widths: [640, 1280, 1536], mobileWidths: [640], width: 1536, height: 880 },
  'HERO-03': hero('HERO-03', 'hero-get-help'), // cartoon
  'HERO-04': hero('HERO-04', 'hero-volunteer'),
  'HERO-05': hero('HERO-05', 'hero-programs'),
  'HERO-06': hero('HERO-06', 'hero-donate'), // cartoon
  'HERO-07': hero('HERO-07', 'hero-stories'), // cartoon
  'HERO-08': hero('HERO-08', 'hero-contact'), // cartoon

  // Home
  'BAND-01': real('BAND-01', '/images/band/band-plating-meals', 320, '50% 20%', 'freshSTART volunteers together in the food pantry.'),
  'BAND-02': real('BAND-02', '/images/band/band-full-table', 322, '50% 30%', 'Volunteers at Pantry Take Over Day.'),
  'BAND-03': real('BAND-03', '/images/band/band-our-volunteers', 320, '50% 0%', 'Two volunteers stocking the pantry shelves.'),
  'FEAT-01': content('FEAT-01', '/images/band/feature-community-table', 1280, 1024, 'A community hall set up for the Community Table dinner.'),

  // About
  'ABOUT-01': { ...content('ABOUT-01', '/images/about/about-first-table', 2400, 1200, 'One folding table set for a small community meal.'), widths: [1280, 2400] },
  // The full group photo, uncropped on the page (client review R8, PRP-16 D5 fallback):
  // the same files as HERO-02, shown at their natural ratio so all eight people are in view.
  'ABOUT-GROUP': {
    ...content('ABOUT-GROUP', '/images/hero/hero-about', 1536, 880, 'Eight freshSTART volunteers smiling together under the event tent, one holding a Community Table flyer.'),
    widths: [640, 1280, 1536],
  },
  'ABOUT-02': real('ABOUT-02', '/images/about/about-todays-kitchen', 320, '67% 30%', 'A volunteer with fresh bread in the freshSTART pantry.'),

  // Programs
  'PROG-01': real('PROG-01', '/images/programs/prog-helps-partner', 322, '50% 30%', 'Volunteers serving at a partner food pantry.'),
  'PROG-02': real('PROG-02', '/images/programs/prog-connects-conversation', 320, '45% 35%', 'A freshSTART volunteer welcoming neighbors in the pantry.'),
  'PROG-03': content('PROG-03', '/images/programs/prog-food-snacks', 1280, 960, ''),
  'PROG-04': content('PROG-04', '/images/programs/prog-supplies', 1280, 960, ''),

  // Get Help (cartoons)
  'HELP-01': content('HELP-01', '/images/help/help-welcome', 1280, 853, 'A volunteer welcoming a neighbor at the door.'),
  'HELP-02': content('HELP-02', '/images/help/help-coffee', 1280, 853, 'Coffee and conversation at the table.'),
  'HELP-03': content('HELP-03', '/images/help/help-dinnertime', 1280, 853, 'Families and neighbors eating together at dinnertime.'),

  // Volunteer & Events
  'VOL-01': real('VOL-01', '/images/volunteer/vol-serving-line', 320, '50% 20%', 'freshSTART volunteers ready for a pantry shift.'),
  'VOL-02': real('VOL-02', '/images/volunteer/vol-huddle', 320, '67% 30%', 'A volunteer sorting bread in the pantry.'),
  'VOL-03': real('VOL-03', '/images/volunteer/vol-packing-bags', 320, '45% 35%', 'A volunteer among the stocked pantry shelves.'),

  // Donate
  'DON-01': content('DON-01', '/images/donate/donate-full-plate', 1280, 1280, 'A hot meal in a take-home box: what your gift provides.'),
  'DON-02': content('DON-02', '/images/donate/donate-dropoff', 1280, 853, 'A neighbor dropping off donated food and supplies.'), // cartoon

  // Defaults, cartoons (event images are wired by the template; see docs/design/template-handoff-event-default-image.md)
  'EVT-01': content('EVT-01', '/images/defaults/event-default-meal', 1280, 720, 'A table set with hot food and many hands reaching in to share.'),
  'EVT-02': content('EVT-02', '/images/defaults/event-default-volunteer', 1280, 720, 'Volunteer aprons hanging ready for the next shift.'),
  'EVT-03': content('EVT-03', '/images/defaults/event-default-drive', 1280, 720, 'Boxes of produce and water ready for a food drive.'),
  'STORY-01': content('STORY-01', '/images/defaults/story-default', 1280, 720, ''),

  // Spot illustrations, cartoons (decorative; the surrounding text carries the meaning)
  'ILL-01': illustration('ILL-01', 'ill-no-events'),
  'ILL-02': illustration('ILL-02', 'ill-success'),
  'ILL-03': illustration('ILL-03', 'ill-404'),
  'ILL-04a': illustration('ILL-04a', 'ill-step-1'),
  'ILL-04b': illustration('ILL-04b', 'ill-step-2'),
  'ILL-04c': illustration('ILL-04c', 'ill-step-3'),
} as const satisfies Record<string, SiteImageEntry>;

export type ImageId = keyof typeof IMAGES;

export const srcFor = (entry: SiteImageEntry, width: number) => `${entry.base}-${width}.webp`;

export const srcSetFor = (entry: SiteImageEntry) =>
  entry.widths.map((w) => `${srcFor(entry, w)} ${w}w`).join(', ');

export const mobileSrcSetFor = (entry: SiteImageEntry) =>
  (entry.mobileWidths ?? []).map((w) => `${entry.base}-m-${w}.webp ${w}w`).join(', ');

/** The largest width at or below `cap` (default 1280), for the plain `src` fallback. */
export const fallbackWidth = (entry: SiteImageEntry, cap = 1280) =>
  [...entry.widths].reverse().find((w) => w <= cap) ?? entry.widths[0];

/** Every public file the manifest references (used by the file-exists test). */
export const allFiles = (entry: SiteImageEntry) => [
  ...entry.widths.map((w) => srcFor(entry, w)),
  ...(entry.mobileWidths ?? []).map((w) => `${entry.base}-m-${w}.webp`),
];
